import { useQuery } from '@tanstack/react-query';
import { signInAs } from '@/api/http';
import type { Person } from '@/api/types';
import { Mark } from '@/components/Layout';
import { Avatar, Loading } from '@/components/ui';

type DevPerson = Person & { email: string; client?: string };

/**
 * Local sign-in: pick who you are. Exists only with portal-bff in dev mode;
 * the real entry is the e-mail link at /login (MK42-5729).
 */
export function DevSignIn() {
  const { data, error } = useQuery({
    queryKey: ['dev-people'],
    queryFn: async (): Promise<DevPerson[]> => {
      const r = await fetch('/api/dev/people');
      if (!r.ok) throw new Error(`portal-bff ответил ${r.status}`);
      return r.json();
    },
  });
  const pick = (p: DevPerson) => {
    signInAs(p.id);
    window.location.assign(p.side === 'studio' ? '/studio' : '/');
  };
  const studio = data?.filter((p) => p.side === 'studio') ?? [];
  const clients = data?.filter((p) => p.side === 'client') ?? [];

  return (
    <div className="grid min-h-app place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <Mark />
          <div>
            <div className="font-semibold">Вход в кабинет</div>
            <div className="text-sm text-muted">Локальная разработка: выберите, кем войти</div>
          </div>
        </div>
        {error ? (
          <p className="text-sm text-overdue">portal-bff недоступен: {(error as Error).message}. Запустите scripts/dev-run.sh.</p>
        ) : !data ? (
          <Loading />
        ) : (
          <div className="space-y-6">
            {[
              ['Студия', studio],
              ['Клиенты', clients],
            ].map(([title, list]) => (
              <section key={title as string}>
                <h2 className="mb-2 text-sm font-semibold">{title as string}</h2>
                {(list as DevPerson[]).length === 0 ? (
                  <p className="text-sm text-muted">Пока никого. Клиенты появятся, когда студия их заведёт.</p>
                ) : (
                  <ul className="divide-y divide-line rounded-lg shadow-hair">
                    {(list as DevPerson[]).map((p) => (
                      <li key={p.id}>
                        <button onClick={() => pick(p)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-panel">
                          <Avatar person={p} />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium">{p.name}</span>
                            <span className="block truncate text-xs text-muted">
                              {p.client ? `${p.client}, ` : ''}
                              {p.email}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
